import axios, { AxiosError, AxiosInstance } from 'axios'
import _ from 'lodash'
import { generateRecaptchaToken } from '../common/recapctcha'
import { PlanningDataRoot, SelectedParcelProps } from '../components/planning-components/slice'
import { PLANNING_SEARCH_PATH, RECAPTCHA_ENABLED } from '../environment'
import RecaptchaRequiredError from '../errors/RecaptchaRequiredError'

const AXIOS_CONFIG = {
  headers: { 'X-RDS-AJAX': 'true', 'Accept-Language': 'en-AU' },
  baseURL: PLANNING_SEARCH_PATH,
  withCredentials: true,
}

class PlanningServiceAPI {

  client: AxiosInstance

  constructor() {
    this.client = axios.create(AXIOS_CONFIG)
  }

  fetchPlanningData = async (payload: SelectedParcelProps) => {
    // require recaptcha
    if (RECAPTCHA_ENABLED) {
      const isV3Captcha = !payload.recaptchaV2Code
      const recaptchaToken = await generateRecaptchaToken()
      return await this.fetchPlanningDataWithRecaptcha(recaptchaToken, payload, isV3Captcha)
    } else {
      return await this.fetchPlanningDataWithoutRecaptcha(payload)
    }
  }

  /**
   * Fetch Planning Data asynchronously. update planningData in the redux store with the results.
   *
   * @returns {Promise<void>}
   */

  fetchPlanningDataWithoutRecaptcha = async (result: SelectedParcelProps) => {
    try {
      let planningData = await this.client.get<PlanningDataRoot>('/data', {
        params: {
          laisKey: result.LAISKEY,
        },
      })
      return planningData.data
    } catch (error: any) {
      return this.handlePlanningDataError(error)
    }
  }

  /**
   * Fetch Planning Data asynchronously. update planningData in the redux store with the results.
   *
   * @returns {Promise<void>}
   */

  fetchPlanningDataWithRecaptcha = async (captchaCode: string, result: SelectedParcelProps, isV3recaptcha: boolean) => {
    try {
      let planningData = await this.client.get<PlanningDataRoot>('/dataWithRecaptcha', {
        params: {
          laisKey: result.LAISKEY,
          [isV3recaptcha ? 'recaptchaV3Key' : 'recaptchaV2Key']: captchaCode,
        },
      })

      return planningData.data
    } catch (error: any) {
      return this.handlePlanningDataError(error)
    }
  }

  handlePlanningDataError = (error: any) => {
    if (_.isEmpty(error.response) || _.isEmpty(error.response?.data) || _.isEmpty(error.response?.data.type)) {
      // Unable to contact the server.
      return {
        error: true,
        errorType: 'INTERNAL_ERROR',
        errorMessage: 'Internal Server Error',
      }
    } else if (error.response?.data.type === 'INVALID_CAPTCHA') {
      // V3 recaptcha failed (low score) force V2 recaptcha
      throw new RecaptchaRequiredError('error.response.data.type', 'Recaptcha failed (V3), manual Recaptcha required! (V2)')
    } else if (error.response?.data.type === 'INVALID_PARCEL') {
      // No parcel found / parcel is not available online, update valuation data with an error.
      return {
        error: true,
        errorType: error.response.data.type,
        errorMessage: error.response.data.errorMessage,
      }
    } else if (error.message !== 'CANCEL') {
      return {
        error: true,
        errorType: 'INTERNAL_ERROR',
        errorMessage: 'Internal Server Error',
      }
    }
  }
}

const planningServiceAPI = new PlanningServiceAPI()

export default planningServiceAPI